/**
 * Canonical revenue/money calculation functions for products.
 *
 * Formulas:
 * - mediaSpend  = price - (price × margin / 100)
 * - grossProfit = price × margin / 100
 * - billedPrice = Client payer → grossProfit; GRO payer → price
 * - total       = billedPrice × qty
 * - netTotal    = price × qty
 */
import { toNumber, round2, toFixed2 } from './numeric'

export interface ProductMoneyFields {
  price: number
  margin: number
  qty: number
  payer: 'GRO' | 'Client'
  mediaSpend: number
  grossProfit: number | string
  billedPrice: number
  total: number
  netTotal: number
}

/**
 * Calculate all money-related fields for a product.
 *
 * @param product - Must have price, margin, qty, payer fields
 * @param clientMargin - Default margin to apply for Client payer products with no margin set
 */
export const calculateProductMoney = (
  product: { price?: unknown; margin?: unknown; qty?: unknown; payer?: string },
  clientMargin: number = 0
): ProductMoneyFields => {
  let margin = toNumber(product.margin)
  if (product.payer === 'Client' && margin === 0 && clientMargin > 0) {
    margin = clientMargin
  }

  const price = toNumber(product.price)
  const qty = toNumber(product.qty) || 1

  const mediaSpend = price - price * (margin / 100)
  const grossProfitMonthly = price - mediaSpend // = price * margin / 100
  const billedMonthly = product.payer === 'Client' ? grossProfitMonthly : price

  return {
    price,
    margin,
    qty,
    payer: product.payer === 'Client' ? 'Client' : 'GRO',
    mediaSpend: round2(mediaSpend),
    grossProfit: toFixed2(grossProfitMonthly),
    billedPrice: round2(billedMonthly),
    total: round2(billedMonthly * qty),
    netTotal: round2(price * qty),
  }
}
